import React from "react";
import {
  IonBadge,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSpinner,
} from "@ionic/react";
import { Redirect } from "react-router-dom";

import Header from "src/components/Header";
import AutocompleteFiscalReporte from "src/components/AutocompleteFiscalReporte";
import AutocompletePuntoServicioReporte from "src/components/AutocompletePuntoServicioReporte";
import EvaluacionAPI, { EstadoEvaluacionReporte, FiscalReporte, PuntoServicioReporte, ReporteJerarquiaItem } from "src/services/evaluacion";
import Storage from "src/services/storage";

const CEDULA_AUTORIZADA = "5355286";

const parseJwtUsuario = (token?: string): string | null => {
  try {
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const json = JSON.parse(atob(padded));
    const usuario = json?.usuario ?? json?.user ?? json?.sub;
    if (usuario === undefined || usuario === null) return null;
    return String(usuario).trim();
  } catch {
    return null;
  }
};

const badgeColor = (estado: EstadoEvaluacionReporte) => {
  if (estado === "FINALIZADA") return "success";
  if (estado === "EN_PROCESO") return "warning";
  return "medium";
};

const badgeText = (estado: EstadoEvaluacionReporte) => {
  if (estado === "FINALIZADA") return "FINALIZADA";
  if (estado === "EN_PROCESO") return "PENDIENTE";
  return "SIN EVALUACIÓN";
};

const EvaluacionDesempenoReporte: React.FC = () => {
  const [authChecked, setAuthChecked] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [autorizado, setAutorizado] = React.useState<boolean | null>(null);
  const [dias, setDias] = React.useState<number>(90);
  const [fiscal, setFiscal] = React.useState<FiscalReporte | null>(null);
  const [ps, setPs] = React.useState<PuntoServicioReporte | null>(null);
  const [data, setData] = React.useState<ReporteJerarquiaItem[]>([]);
  const [rango, setRango] = React.useState<{ desde?: string; hasta?: string }>({});

  React.useEffect(() => {
    const cargarAuth = async () => {
      try {
        const sesion = await Storage.getSesion();
        const cedula = parseJwtUsuario(sesion?.token) || "";
        const ok = String(cedula).trim() === CEDULA_AUTORIZADA;
        setAutorizado(ok);
      } finally {
        setAuthChecked(true);
      }
    };

    cargarAuth();
  }, []);

  React.useEffect(() => {
    // Al cambiar el fiscal, limpiar el PS seleccionado (para evitar filtros inconsistentes)
    setPs(null);
  }, [fiscal?.cedula]);

  React.useEffect(() => {
    const cargarReporte = async () => {
      if (!autorizado) return;
      if (!fiscal?.cedula) {
        setData([]);
        setRango({});
        return;
      }

      setLoading(true);
      try {
        const resp = await EvaluacionAPI.reporteJerarquia(dias, fiscal.cedula, ps?.id);
        setData(resp?.datos || []);
        setRango({ desde: resp?.desde, hasta: resp?.hasta });
      } finally {
        setLoading(false);
      }
    };

    cargarReporte();
  }, [autorizado, dias, fiscal, ps]);

  if (authChecked && autorizado === false) {
    return <Redirect to="/" />;
  }

  return (
    <IonPage>
      <Header titulo="Reporte Evaluación de Desempeño" />
      <IonContent id="evaluacion-de-desempeno-reporte" className="ion-padding">
        <div className="container">
          <AutocompleteFiscalReporte
            label="Fiscal"
            value={fiscal}
            setValue={setFiscal}
            disabled={!authChecked || autorizado !== true}
          />

          <div style={{ marginTop: 12 }}>
            <AutocompletePuntoServicioReporte
              label="Punto de Servicio"
              fiscalCedula={fiscal?.cedula}
              value={ps}
              setValue={setPs}
              disabled={!authChecked || autorizado !== true || !fiscal?.cedula}
            />
          </div>

          {!authChecked && (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 16 }}>
              <IonSpinner name="crescent" />
            </div>
          )}

          {authChecked && autorizado === true && !fiscal?.cedula && (
            <p style={{ marginTop: 12 }}>
              Seleccione un fiscal para ver el reporte.
            </p>
          )}

          {authChecked && autorizado === true && fiscal?.cedula && (
            <p style={{ marginTop: 12 }}>
              Ventana: {rango.desde || "-"} a {rango.hasta || "-"} (últimos {dias} días)
            </p>
          )}

          {loading && (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 16 }}>
              <IonSpinner name="crescent" />
            </div>
          )}

          {data.map((f) => {
            const fiscalKey = String(f?.fiscal?.cedula || f?.fiscal?.nombre || "");
            return (
              <div key={fiscalKey} style={{ marginBottom: 16 }}>
                <h3 style={{ marginBottom: 4 }}>
                  {f?.fiscal?.nombre || "Fiscal"} ({f?.fiscal?.cedula})
                </h3>

                <p style={{ marginTop: 0 }}>
                  Total: {f?.resumen?.total || 0} | Finalizadas: {f?.resumen?.finalizadas || 0} | Pendientes: {f?.resumen?.pendientes || 0} | Sin evaluación: {f?.resumen?.sin_evaluacion || 0}
                </p>

                {/* Supervisores (si existen) */}
                {(f?.supervisores || []).map((s) => {
                  const sup = s?.supervisor;
                  const supKey = `sup-${sup?.legajo || sup?.cedula || sup?.nombre}`;
                  const psSup = (sup as any)?.puntos_servicios as string[] | undefined;
                  const psSupTexto = Array.isArray(psSup) && psSup.length ? psSup.join(", ") : "";
                  return (
                    <div key={supKey} style={{ marginBottom: 12 }}>
                      <IonList>
                        <IonItem lines="full">
                          <IonLabel>
                            <strong>Supervisor:</strong> {sup?.nombre || "-"} (Legajo {sup?.legajo || "-"})
                            {psSupTexto ? (
                              <div style={{ marginTop: 4 }}>
                                <strong>PS:</strong> {psSupTexto}
                              </div>
                            ) : null}
                          </IonLabel>
                          <IonBadge color={badgeColor(sup?.estado as EstadoEvaluacionReporte)}>
                            {badgeText(sup?.estado as EstadoEvaluacionReporte)}
                          </IonBadge>
                        </IonItem>

                        {(s?.operarios || []).map((op) => {
                          const opKey = `op-${sup?.legajo || "x"}-${op?.legajo || op?.nombre}`;
                          const psOp = (op as any)?.puntos_servicios as string[] | undefined;
                          const psOpTexto = Array.isArray(psOp) && psOp.length ? psOp.join(", ") : "";
                          return (
                            <IonItem key={opKey} lines="none">
                              <IonLabel>
                                Operario: {op?.nombre || "-"} (Legajo {op?.legajo || "-"})
                                {psOpTexto ? (
                                  <div style={{ marginTop: 2 }}>
                                    <strong>PS:</strong> {psOpTexto}
                                  </div>
                                ) : null}
                              </IonLabel>
                              <IonBadge color={badgeColor(op?.estado as EstadoEvaluacionReporte)}>
                                {badgeText(op?.estado as EstadoEvaluacionReporte)}
                              </IonBadge>
                            </IonItem>
                          );
                        })}
                      </IonList>
                    </div>
                  );
                })}

                {/* Operarios directos (cuando no hay supervisor) */}
                {(f?.operarios || []).length > 0 && (
                  <IonList>
                    {(f?.operarios || []).map((op) => {
                      const opKey = `op-dir-${op?.legajo || op?.nombre}`;
                      const psOp = (op as any)?.puntos_servicios as string[] | undefined;
                      const psOpTexto = Array.isArray(psOp) && psOp.length ? psOp.join(", ") : "";
                      return (
                        <IonItem key={opKey} lines="none">
                          <IonLabel>
                            Operario: {op?.nombre || "-"} (Legajo {op?.legajo || "-"})
                            {psOpTexto ? (
                              <div style={{ marginTop: 2 }}>
                                <strong>PS:</strong> {psOpTexto}
                              </div>
                            ) : null}
                          </IonLabel>
                          <IonBadge color={badgeColor(op?.estado as EstadoEvaluacionReporte)}>
                            {badgeText(op?.estado as EstadoEvaluacionReporte)}
                          </IonBadge>
                        </IonItem>
                      );
                    })}
                  </IonList>
                )}
              </div>
            );
          })}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default EvaluacionDesempenoReporte;
